import type { ItemProps } from "@bedrock/domain";

export type TextDownloadProps = Extract<ItemProps, { kind: "document" | "sticky-note" }>;

export interface TextDownload {
  filename: string;
  mimeType: string;
  content: string;
}

const DEFAULT_DOCUMENT_TITLE = "Untitled document";
const MAX_FILENAME_LENGTH = 80;

export function textDownloadForItem(props: TextDownloadProps, markdown = props.markdown): TextDownload {
  if (props.kind === "document") {
    const title = props.title.trim();
    return {
      filename: `${safeFilename(title, DEFAULT_DOCUMENT_TITLE)}.md`,
      mimeType: "text/markdown;charset=utf-8",
      content: withDocumentTitle(title, markdown)
    };
  }
  const content = markdownToText(markdown);
  return {
    filename: `${safeFilename(content.split("\n").find((line) => line.trim()) ?? "", "Note")}.txt`,
    mimeType: "text/plain;charset=utf-8",
    content
  };
}

export function downloadTextFile({ filename, mimeType, content }: TextDownload): void {
  const url = URL.createObjectURL(new Blob([content], { type: mimeType }));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

// The canvas shows a document's title above its body and hides a matching leading heading,
// so the file needs the title as its first heading to read the same outside Bedrock.
function withDocumentTitle(title: string, markdown: string): string {
  if (!title || title === DEFAULT_DOCUMENT_TITLE) return markdown;
  const firstLine = markdown.split("\n").find((line) => line.trim());
  const firstHeading = firstLine ? /^#\s+(.*?)(?:\s+#+)?\s*$/.exec(firstLine.trim())?.[1] : undefined;
  if (firstHeading === title) return markdown;
  return markdown.trim() ? `# ${title}\n\n${markdown}` : `# ${title}\n`;
}

// Unlike markdownToPlainText, which flattens a note into one search-friendly line,
// this keeps the line structure so the .txt reads like the note on the canvas.
function markdownToText(markdown: string): string {
  return markdown
    .replace(/\r\n?/g, "\n")
    .split("\n")
    .filter((line) => !/^\s*(```|~~~)/.test(line))
    .map((line) => line
      .replace(/^(\s*)#{1,6}\s+/, "$1")
      .replace(/^(\s*)>\s?/, "$1")
      .replace(/!\[([^\]]*)\]\([^)]*\)/g, "$1")
      .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
      .replace(/(\*\*\*|___|\*\*|__|==|\+\+|~~|`)(.+?)\1/g, "$2")
      .replace(/(^|[^\w*])([*_])(?=\S)(.+?)(?<=\S)\2(?![\w*])/g, "$1$3"))
    .join("\n")
    .trim();
}

function safeFilename(value: string, fallback: string): string {
  // eslint-disable-next-line no-control-regex
  const cleaned = value.replace(/[\\/:*?"<>|\u0000-\u001f]/g, " ").replace(/\s+/g, " ").trim().replace(/^\.+/, "").slice(0, MAX_FILENAME_LENGTH).trim();
  return cleaned || fallback;
}
